import { LocalUser } from '@lib/entities/Auth';
import { isPlaceholderPhone, unformatPhone } from '@lib/utils/strings';

import { BrazeInstance, BrazeUserAttributes, setBrazeUserAttributes } from './braze';

/**
 * JSAD-7144. Keeps the Braze profile's standard attributes (email, first name, last name,
 * phone) in step with the signed-in user. Written by the SDK right after `changeUser`, so a
 * new account is reachable in Braze within seconds instead of after the daily batch.
 *
 * Lives outside React for the same reasons as brazeAuth.ts: useBraze is mounted from ~22
 * components, so its identity effect runs that many times per user change, and useAuth's
 * logout teardown needs to reset the state.
 */

export type BrazeProfileSource = Pick<
    LocalUser,
    'id' | 'email' | 'firstName' | 'lastName' | 'phone'
>;

const US_PHONE_DIGITS = 10;

const trimmed = (value: string | undefined): string | undefined => {
    const clean = value?.trim();
    return clean ? clean : undefined;
};

/**
 * OJS stores a bare 10-digit US number, or the all-zeroes placeholder for an account that
 * has not completed phone verification. Braze wants E.164 and must never see the
 * placeholder, so anything that is not exactly ten digits is left off the profile.
 */
const toE164 = (phone: string | undefined): string | undefined => {
    if (!phone || isPlaceholderPhone(phone)) {
        return undefined;
    }

    const digits = unformatPhone(phone);
    return digits.length === US_PHONE_DIGITS ? `+1${digits}` : undefined;
};

/**
 * The attributes worth writing for `user`. A field that is missing or blank is omitted,
 * never sent as null or '', so Braze keeps whatever it already holds (Apple sign-in can
 * hand us an account with no name).
 */
export const buildBrazeProfile = (user: BrazeProfileSource): BrazeUserAttributes => {
    const attrs: BrazeUserAttributes = {};

    const email = trimmed(user.email);
    if (email) {
        attrs.email = email;
    }

    const firstName = trimmed(user.firstName);
    if (firstName) {
        attrs.firstName = firstName;
    }

    const lastName = trimmed(user.lastName);
    if (lastName) {
        attrs.lastName = lastName;
    }

    const phone = toE164(user.phone);
    if (phone) {
        attrs.phone = phone;
    }

    return attrs;
};

let lastSynced: string | undefined;

const snapshotKey = (userId: number, attrs: BrazeUserAttributes) =>
    JSON.stringify([userId, attrs.email, attrs.firstName, attrs.lastName, attrs.phone]);

/**
 * Writes `user`'s attributes to Braze unless the identical snapshot was already written
 * for that user id. Returns whether a write happened.
 *
 * Keying on the snapshot rather than the id alone is what makes a profile edit re-write
 * (the refreshed user carries new values) while 22 concurrent mounts of the same user do
 * not. Including the id means A -> B -> A on one device re-writes for A, which is right:
 * changeUser(A) has just re-pointed the SDK at that profile.
 */
export const syncBrazeProfile = (braze: BrazeInstance, user: BrazeProfileSource): boolean => {
    const attrs = buildBrazeProfile(user);
    const key = snapshotKey(user.id, attrs);

    if (key === lastSynced) {
        return false;
    }

    setBrazeUserAttributes(braze, attrs);
    lastSynced = key;
    return true;
};

/**
 * Forgets the last-written snapshot. Called from useAuth's logout teardown so the next
 * sign-in on this device writes again, even when it is the same customer returning.
 */
export const clearBrazeProfile = () => {
    lastSynced = undefined;
};
